// utils/auth.ts
let authCheckPromise: Promise<boolean> | null = null;
let _isAuthenticating = false;
const BaseUrl = import.meta.env.VITE_SSO_AUTH_URL;
/**
 * Returns **true** if the user has a valid session.
 * Works for **HttpOnly** `sid` cookies – we always hit the backend.
 */
export const checkAuthStatus = async (context = 'unknown'): Promise<boolean> => {
  const log = (...args: any[]) =>
    console.log(`[Auth][${new Date().toISOString()}] (${context})`, ...args);

  log('Checking auth status...');

  // ---- 1. Reuse an in-flight request ----
  if (authCheckPromise) {
    log('Re-using in-progress auth check');
    return authCheckPromise;
  }

  // ---- 2. Mark as "checking" ----
  _isAuthenticating = true;

  // ---- 3. Perform the actual validation ----
  authCheckPromise = (async () => {
    try {
      log('Calling session validation endpoint...');

      const response = await fetch(`${BaseUrl}/auth/session/validate?client-id=17`, {
        method: 'GET',
        credentials: 'include',               
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
        },
      });

      const isAuthenticated = response.ok;
      log('Session validation result', {
        ok: response.ok,
        status: response.status,
        isAuthenticated,
      });

      return isAuthenticated;
    } catch (err) {
      console.error('[Auth] Validation failed', err);
      return false;
    } finally {
      _isAuthenticating = false;
      authCheckPromise = null;
    }
  })();

  return authCheckPromise;
};

/**
 * Optional helper – tells you if a check is currently running.
 */
export const isAuthenticating = (): boolean => _isAuthenticating;

/**
 * Log the user out – delete cookie + tell backend.
 */
export const clearSession = async (): Promise<void> => {
  // Expire the cookie (works for non-HttpOnly; HttpOnly is cleared server-side)
  document.cookie = 'sid=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Lax';
  document.cookie = 'sid=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT;';

  // Invalidate on the server
  await fetch(`${BaseUrl}/auth/oauth/logout`, { 
    method: 'POST',
    credentials: 'include',
  }).catch(() => {
    /* network errors are non-fatal */
  });
};